// Org 360 dashboard API layer.
//
// This page is an AGGREGATOR: it does not introduce a single mega-endpoint.
// Instead it reuses the existing per-org admin endpoints so the slow calls
// (billing detail, Langfuse live fetch) never block the fast ones. Every
// wrapper here leans on the shared `fetchWithFallback` helper from
// OrgUsageAnalytics/api.js, which handles the double-`/admin` prefix, env
// routing (prod/dev), userId injection, and timeout/fallback behavior.

import {
  fetchWithFallback,
  fetchBlobWithFallback,
  dateRangeParams,
} from "../OrgUsageAnalytics/api";

export { dateRangeParams };

/**
 * Combined usage analytics for one org (summary + members + usage-over-time +
 * feature breakdown + users-by-activity + recent loan access) in a single
 * backend round-trip. Backed by `/admin/usage-analytics/:org/dashboard`.
 */
export const fetchOrgUsageDashboard = (organizationId, params, env) =>
  fetchWithFallback(`/usage-analytics/${organizationId}/dashboard`, params, {
    env,
    timeoutMs: 90000,
  });

/**
 * Per-org billing usage detail. Same endpoint the Enterprise Billing detail
 * page uses; returns an array of monthly buckets (one element for a specific
 * month). Heavy — scans all loans + selective Storage hydration — so this is
 * only fetched when the Billing section scrolls into view.
 */
export const fetchOrgBillingDetail = (organizationId, params, env) =>
  fetchWithFallback(
    `/billing-usage/${encodeURIComponent(organizationId)}`,
    params,
    {
      env,
      timeoutMs: 120000,
    },
  );

/**
 * AI usage limits for one org. There's no dedicated single-org route, but the
 * usage-limits endpoint runs a global name/ID search when `search` is present,
 * so passing the exact org id returns just that org's row(s). We pick the
 * matching id out of the result.
 */
export const fetchOrgLimits = async (organizationId, env) => {
  const json = await fetchWithFallback(
    "/organizations/usage-limits",
    { search: organizationId },
    { env, timeoutMs: 60000 },
  );
  const orgs = Array.isArray(json?.orgs) ? json.orgs : [];
  const org = orgs.find((o) => o.id === organizationId) || orgs[0] || null;
  return {
    org,
    quotaWindowMs: json?.quotaWindowMs || null,
    globalDefaults: json?.globalDefaults || null,
    env: json?.env || null,
  };
};

// Langfuse / ExtendAI accept a custom ISO window. We always send an explicit
// range derived from the page's day selector so the AI sections line up with
// the rest of the dashboard regardless of the preset tokens the standalone
// page happens to expose.
const customWindowParams = (days) => {
  const to = new Date();
  const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return {
    timeWindow: "custom",
    fromTimestamp: from.toISOString(),
    toTimestamp: to.toISOString(),
  };
};

/**
 * Langfuse LLM analytics scoped to one org (cost, calls, latency, errors, and
 * leaderboards). Heavy live fetch for wide windows — lazy-loaded.
 */
export const fetchOrgLangfuse = (organizationId, env, days) =>
  fetchWithFallback(
    "/observability/langfuse-analytics",
    {
      organizationId,
      environment: env,
      rowLimit: 50,
      ...customWindowParams(days),
    },
    { env, timeoutMs: 120000 },
  );

/**
 * ExtendAI OCR usage scoped to one org (traces, pages, cost). Reads
 * `extendAiUsageLogs` server-side. Lazy-loaded.
 */
export const fetchOrgExtendAi = (organizationId, env, days) =>
  fetchWithFallback(
    "/observability/extend-ai-analytics",
    {
      organizationId,
      environment: env,
      ...customWindowParams(days),
    },
    { env, timeoutMs: 120000 },
  );

/**
 * Integrations this org is connected to (LOS, CRM, pricing, e-sign, OCR), read
 * off the org document. Backed by `/admin/integrations/status`; returns only
 * integrations the org has actually engaged with, each with a normalized status.
 */
export const fetchOrgIntegrations = (organizationId, env) =>
  fetchWithFallback(
    "/integrations/status",
    { organizationId },
    { env, timeoutMs: 60000 },
  );

/**
 * Processing-checklist inactivity re-engagement status for one org: PC metrics
 * (last run, days since, all-time + 30-day run counts), current alert settings,
 * resolved owner/admin recipients, eligibility, and recent send history.
 * Backed by `/admin/pc-inactivity/:org/status`.
 */
export const fetchOrgPcInactivityStatus = (organizationId, env) =>
  fetchWithFallback(`/pc-inactivity/${organizationId}/status`, {}, {
    env,
    timeoutMs: 60000,
  });

/**
 * Persist edited re-engagement email settings (enabled, threshold, cooldown,
 * subject, HTML/plain-text body) for one org. Returns the refreshed status.
 */
export const updateOrgPcInactivitySettings = (organizationId, settings, env) =>
  fetchWithFallback(`/pc-inactivity/${organizationId}/settings`, {}, {
    env,
    method: "PUT",
    body: { settings },
    timeoutMs: 60000,
  });

/**
 * Dry-run evaluation for one org: reports whether it would send and to whom,
 * WITHOUT claiming the cooldown or emailing anyone.
 */
export const previewOrgPcInactivity = (organizationId, env) =>
  fetchWithFallback(`/pc-inactivity/${organizationId}/preview`, {}, {
    env,
    method: "POST",
    timeoutMs: 60000,
  });

/**
 * At-risk org directory: active orgs ranked by days since their last PC run.
 * Backed by `/admin/pc-inactivity/overview`.
 */
export const fetchPcInactivityOverview = (params, env) =>
  fetchWithFallback("/pc-inactivity/overview", params, {
    env,
    timeoutMs: 120000,
  });

/**
 * Shared re-engagement email template used for operator-initiated bulk sends
 * (subject, HTML/plain-text body). Backed by `/admin/pc-inactivity/template`.
 */
export const fetchPcInactivityTemplate = (env) =>
  fetchWithFallback("/pc-inactivity/template", {}, { env, timeoutMs: 60000 });

/** Persist the shared bulk-send email template. Returns the refreshed template. */
export const updatePcInactivityTemplate = (template, env) =>
  fetchWithFallback("/pc-inactivity/template", {}, {
    env,
    method: "PUT",
    body: { template },
    timeoutMs: 60000,
  });

/**
 * Send a one-off test email (rendered with sample tokens) to an address so the
 * operator can see the email before a bulk send. Falls back to the saved
 * template when content fields are omitted.
 */
export const sendTestPcInactivityEmail = (payload, env) =>
  fetchWithFallback("/pc-inactivity/template/test", {}, {
    env,
    method: "POST",
    body: payload,
    timeoutMs: 60000,
  });

/**
 * Send (or dry-run preview) the shared re-engagement template to an explicit
 * list of selected orgs. Waives the disabled/threshold gates but keeps the
 * cooldown, recipient, active-org, and test/internal safety gates.
 * Backed by `/admin/pc-inactivity/bulk-send`.
 */
export const bulkSendPcInactivity = (organizationIds, { dryRun } = {}, env) =>
  fetchWithFallback("/pc-inactivity/bulk-send", {}, {
    env,
    method: "POST",
    body: { organizationIds, dryRun: dryRun === true },
    timeoutMs: 180000,
  });

/**
 * Adoption-gaps ("never used") directory: active orgs that have NEVER adopted a
 * given product surface. `signal` is one of `never_guidelines`,
 * `never_trained`, or `never_ask_addy`. Backed by
 * `/admin/adoption-gaps/overview`.
 */
export const fetchAdoptionGapsOverview = (params, env) =>
  fetchWithFallback("/adoption-gaps/overview", params, {
    env,
    timeoutMs: 120000,
  });

/**
 * The outreach template for ONE adoption signal. Each signal keeps its own
 * copy — the shared PC template talks about the last checklist run, which is
 * not why an org appears on a "never used" list.
 */
export const fetchAdoptionGapsTemplate = (signal, env) =>
  fetchWithFallback("/adoption-gaps/template", { signal }, {
    env,
    timeoutMs: 60000,
  });

/** Persist one signal's outreach template. Returns the refreshed template. */
export const updateAdoptionGapsTemplate = (signal, template, env) =>
  fetchWithFallback("/adoption-gaps/template", {}, {
    env,
    method: "PUT",
    body: { signal, template },
    timeoutMs: 60000,
  });

/** Send a one-off rendered test email for one signal's template. */
export const sendTestAdoptionGapsEmail = (payload, env) =>
  fetchWithFallback("/adoption-gaps/template/test", {}, {
    env,
    method: "POST",
    body: payload,
    timeoutMs: 60000,
  });

/**
 * Send (or dry-run) a signal's template to selected orgs. Keeps the cooldown,
 * recipient, active-org and internal-org gates, and re-checks the signal at
 * send time so an org that has since adopted is skipped.
 */
export const bulkSendAdoptionGaps = (signal, organizationIds, { dryRun } = {}, env) =>
  fetchWithFallback("/adoption-gaps/bulk-send", {}, {
    env,
    method: "POST",
    body: { signal, organizationIds, dryRun: dryRun === true },
    timeoutMs: 180000,
  });

/**
 * Voice assistant settings for one org (models, voice, wording, voicemail
 * policy, call behaviour, time zone), with the defaults, the allowed model
 * options and the limits the form needs. Backed by
 * `/admin/voice-settings/:org`.
 */
export const fetchOrgVoiceSettings = (organizationId, env) =>
  fetchWithFallback(`/voice-settings/${encodeURIComponent(organizationId)}`, {}, {
    env,
    timeoutMs: 60000,
  });

/** Persist a voice-settings patch for one org. Returns the saved settings. */
export const updateOrgVoiceSettings = (organizationId, settings, env) =>
  fetchWithFallback(`/voice-settings/${encodeURIComponent(organizationId)}`, {}, {
    env,
    method: "PUT",
    body: { settings },
    timeoutMs: 60000,
  });

/** The org's newest AI phone calls with the models used and the cost. */
export const fetchOrgRecentVoiceCalls = (organizationId, env) =>
  fetchWithFallback(
    `/voice-settings/${encodeURIComponent(organizationId)}/recent-calls`,
    {},
    { env, timeoutMs: 60000 },
  );

/** One voice's preview clip (WAV) relayed by the backend from the voice API. */
export const fetchOrgVoicePreview = (provider, voiceId, env) =>
  fetchBlobWithFallback(
    `/voice-settings/voices/${encodeURIComponent(provider)}/${encodeURIComponent(voiceId)}/preview`,
    {},
    { env, timeoutMs: 45000 },
  );
